/**
 * <ReferralDashboardSkeleton> — la forme du tableau de parrainage pendant son chargement.
 *
 * Les deux colonnes de la page (une seule sur un téléphone). À gauche, la carte de partage — son
 * bandeau (le titre et Zoé), puis le lien et « Partagez via » sous la forme que leurs molécules
 * attendent (`<ReferralLinkRowSkeleton>`, `<ReferralShareActionsSkeleton>`) — puis les statistiques
 * du lien, leurs quatre tuiles. À droite, « Comment ça marche » et ses trois étapes, puis la ligne
 * « Voir les conditions ». Les intitulés de section sont les vrais.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Row } from "../../atoms/containers/Row"
import { columnStyles } from "../../atoms/containers/columns"
import { Stack } from "../../atoms/containers/Stack"
import { Skeleton } from "../../atoms/Skeleton"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Card } from "../../molecules/Card"
import { CardList, CardListRow } from "../../molecules/CardList"
import { ReferralLinkRowSkeleton } from "../../molecules/ReferralLinkRow/ReferralLinkRowSkeleton"
import { ReferralShareActionsSkeleton } from "../../molecules/ReferralShareActions/ReferralShareActionsSkeleton"

/** Zoé dans le bandeau de partage. */
const MASCOT_SIZE = 70

/** La bulle d'icône d'une étape. */
const STEP_BUBBLE = 36

function ShareCardBones() {
    return (
        <Card p="0" style={styles.shareCard}>
            <View style={styles.shareHeader}>
                <Row justify="between" align="center">
                    <Stack gap="1" style={styles.fill}>
                        <Skeleton textSize="text-xl" width={160} />
                        <Skeleton textSize="text-sm" width={110} />
                    </Stack>
                    <Skeleton shape="circle" size={MASCOT_SIZE} />
                </Row>
            </View>
            <Stack gap="4" style={styles.shareBody}>
                <ReferralLinkRowSkeleton />
                <ReferralShareActionsSkeleton />
            </Stack>
        </Card>
    )
}

function StatsBones() {
    return (
        <Card p="4">
            <View style={styles.statsGrid}>
                {[0, 1, 2, 3].map((index) => (
                    <View key={index} style={styles.statsCell}>
                        <Skeleton textSize="text-2xl" width={48} />
                        <Skeleton textSize="text-sm" width="70%" />
                    </View>
                ))}
            </View>
        </Card>
    )
}

function StepsBones() {
    return (
        <CardList>
            {(["70%", "85%", "80%"] as const).map((width) => (
                <CardListRow key={width}>
                    <Row gap="3" align="center">
                        <Skeleton shape="circle" size={STEP_BUBBLE} />
                        <View style={styles.fill}>
                            <Skeleton textSize="text-md" width={width} />
                        </View>
                    </Row>
                </CardListRow>
            ))}
        </CardList>
    )
}

export function ReferralDashboardSkeleton() {
    return (
        <View style={columnStyles.columns}>
            <Stack gap="4" style={columnStyles.column}>
                <ShareCardBones />
                <Stack gap="2">
                    <SectionLabel>Statistiques de votre lien</SectionLabel>
                    <StatsBones />
                </Stack>
            </Stack>
            <Stack gap="4" style={columnStyles.column}>
                <Stack gap="2">
                    <SectionLabel>Comment ça marche</SectionLabel>
                    <StepsBones />
                </Stack>
                <View style={styles.terms}>
                    <Skeleton textSize="text-md" width={140} />
                </View>
            </Stack>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    fill: { flex: 1, flexShrink: 1 },
    shareCard: { overflow: "hidden" },
    /** Le bandeau de `<ReferralShareHeader>` : ses retraits, sans son aplat marine. */
    shareHeader: { padding: theme.spacing["4"] },
    shareBody: { padding: theme.spacing["4"] },
    statsGrid: { flexDirection: "row", flexWrap: "wrap", gap: theme.spacing["3"] },
    statsCell: { flexGrow: 1, flexBasis: "45%" },
    /** « Voir les conditions » : une ligne centrée, sous les étapes. */
    terms: { alignItems: "center", paddingVertical: theme.spacing["2"] },
}))
